import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { DigestSettings, ScheduleResponse } from '../../../shared/api.js';
import { api } from '../lib/api';

const QUERY_KEY = ['schedule'];
const PREVIEW_KEY = ['schedule-preview'];

const lines = (text: string) =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

function Recipients({
  status,
  draft,
  onChange,
}: {
  status: ScheduleResponse;
  draft: DigestSettings;
  onChange: (recipients: string[]) => void;
}) {
  const admins = status.accounts.find((a) => a.id === draft.account)?.admins ?? [];
  const others = draft.recipients.filter((r) => !admins.includes(r));
  const [extra, setExtra] = useState('');
  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...draft.recipients, id] : draft.recipients.filter((r) => r !== id));
  return (
    <div className="schedule__field">
      <span>Кому</span>
      {admins.length === 0 && (
        <span className="muted small">У аккаунта нет админов: добавьте адрес ниже.</span>
      )}
      {admins.map((id) => (
        <label key={id} className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={draft.recipients.includes(id)}
            onChange={(e) => toggle(id, e.target.checked)}
          />
          админ {id}
        </label>
      ))}
      {others.map((r) => (
        <label key={r} className="calendar-list__toggle">
          <input type="checkbox" checked onChange={() => toggle(r, false)} />
          {r}
        </label>
      ))}
      <div className="inline-form">
        <input
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          placeholder="@username или id"
        />
        <button
          type="button"
          className="btn btn--small"
          disabled={!extra.trim() || draft.recipients.includes(extra.trim())}
          onClick={() => {
            onChange([...draft.recipients, extra.trim()]);
            setExtra('');
          }}
        >
          Добавить
        </button>
      </div>
      <span className="muted small">
        Получатель должен хотя бы раз написать аккаунту в личку, иначе Telegram не даст отправить по
        id.
      </span>
    </div>
  );
}

function Renames({
  renames,
  onChange,
}: {
  renames: DigestSettings['renames'];
  onChange: (renames: DigestSettings['renames']) => void;
}) {
  const set = (n: number, patch: Partial<DigestSettings['renames'][number]>) =>
    onChange(renames.map((r, i) => (i === n ? { ...r, ...patch } : r)));
  return (
    <div className="schedule__field">
      <span>Замены названий</span>
      <span className="muted small">
        Если название содержит «было», в сообщении пишется «стало» (например, имя в нужном падеже).
      </span>
      {renames.map((r, n) => (
        <div key={n} className="schedule__rename">
          <input
            className="schedule__from"
            value={r.from}
            aria-label="было"
            onChange={(e) => set(n, { from: e.target.value })}
          />
          <span className="schedule__arrow" aria-hidden="true">
            →
          </span>
          <input
            className="schedule__to"
            value={r.to}
            aria-label="стало"
            onChange={(e) => set(n, { to: e.target.value })}
          />
          <button
            type="button"
            className="btn btn--small btn--ghost schedule__del"
            aria-label="Удалить"
            onClick={() => onChange(renames.filter((_, i) => i !== n))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn--small"
        onClick={() => onChange([...renames, { from: '', to: '' }])}
      >
        Добавить замену
      </button>
    </div>
  );
}

/** Settings → Расписание: the evening message with tomorrow's schedule (owner only). */
export function ScheduleSection() {
  const status = useQuery({ queryKey: QUERY_KEY, queryFn: api.schedule });
  if (status.isPending) return <p className="muted">Загрузка…</p>;
  if (status.isError) return <p className="text-error">{status.error.message}</p>;
  // A new editor after every save: its draft starts from the saved settings.
  return <ScheduleEditor key={JSON.stringify(status.data.settings)} data={status.data} />;
}

function ScheduleEditor({ data }: { data: ScheduleResponse }) {
  const queryClient = useQueryClient();
  const preview = useQuery({ queryKey: PREVIEW_KEY, queryFn: api.schedulePreview });
  const [draft, setDraft] = useState<DigestSettings>(data.settings);
  const [exclude, setExclude] = useState(data.settings.exclude.join('\n'));

  const save = useMutation({
    mutationFn: (settings: DigestSettings) => api.scheduleSave(settings),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: PREVIEW_KEY });
    },
  });
  const send = useMutation({ mutationFn: api.scheduleSend });
  const patch = (p: Partial<DigestSettings>) => setDraft({ ...draft, ...p });
  const full = {
    ...draft,
    exclude: lines(exclude),
    renames: draft.renames.filter((r) => r.from.trim()),
  };
  const changed = JSON.stringify(full) !== JSON.stringify(data.settings);

  return (
    <section className="schedule">
      <h2>Расписание</h2>
      <p className="muted">
        Каждый вечер — сообщение в Telegram с расписанием на завтра. Пустой день не отправляется, за
        один день — не больше одного раза.
      </p>
      {!data.sendsHere && (
        <p className="muted small">
          Этот сервер не отправляет (TELEGRAM_ARCHIVE выключен); отправляет прод.
        </p>
      )}

      <div className="card form schedule__form">
        <label className="calendar-list__toggle">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Отправлять каждый вечер
        </label>
        <label>
          Время
          <input
            type="time"
            value={draft.time}
            onChange={(e) => patch({ time: e.target.value })}
            required
          />
        </label>
        <label>
          С какого аккаунта
          <select
            value={draft.account}
            onChange={(e) => {
              const account = e.target.value;
              const admins = data.accounts.find((a) => a.id === account)?.admins ?? [];
              patch({ account, recipients: admins });
            }}
          >
            {!draft.account && <option value="">— выберите —</option>}
            {data.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label} ({a.id})
              </option>
            ))}
          </select>
        </label>
        <Recipients status={data} draft={draft} onChange={(recipients) => patch({ recipients })} />

        <div className="schedule__field">
          <span>Календари</span>
          {data.calendars.map((c) => (
            <label key={c.id} className="calendar-list__toggle">
              <input
                type="checkbox"
                checked={draft.calendars.includes(c.id)}
                onChange={(e) =>
                  patch({
                    calendars: e.target.checked
                      ? [...draft.calendars, c.id]
                      : draft.calendars.filter((id) => id !== c.id),
                  })
                }
              />
              {c.name}
            </label>
          ))}
        </div>

        <label>
          Не включать события, где в названии есть{' '}
          <span className="muted small">(по строке на слово)</span>
          <textarea rows={4} value={exclude} onChange={(e) => setExclude(e.target.value)} />
        </label>
        <label>
          Имя, которое убрать из названий{' '}
          <span className="muted small">(«// Геннадий», «Маникюр Геннадий»)</span>
          <input value={draft.ownerName} onChange={(e) => patch({ ownerName: e.target.value })} />
        </label>
        <Renames renames={draft.renames} onChange={(renames) => patch({ renames })} />
        <label>
          Начало сообщения
          <textarea
            rows={3}
            value={draft.header}
            onChange={(e) => patch({ header: e.target.value })}
          />
        </label>
        <label>
          Конец сообщения
          <input value={draft.footer} onChange={(e) => patch({ footer: e.target.value })} />
        </label>

        {save.isError && <p className="text-error small">{save.error.message}</p>}
        <div className="actions-row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate(full)}
          >
            {save.isPending ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>

      <div className="card schedule__preview">
        <div className="card__header">
          <h3>Сообщение на завтра{preview.data && `, ${preview.data.date}`}</h3>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => void preview.refetch()}
          >
            Обновить
          </button>
        </div>
        {changed && <p className="muted small">Показано по сохранённым настройкам.</p>}
        {preview.isError && <p className="text-error small">{preview.error.message}</p>}
        {preview.data &&
          (preview.data.text ? (
            <pre className="schedule__text">{preview.data.text}</pre>
          ) : (
            <p className="muted">Завтра нет событий для сообщения — оно не отправится.</p>
          ))}
        {preview.data && preview.data.skipped.length > 0 && (
          <details>
            <summary className="small muted">Не вошло: {preview.data.skipped.length}</summary>
            <ul className="small muted">
              {preview.data.skipped
                .filter((s) => s.reason !== 'calendar')
                .map((s, n) => (
                  <li key={n}>
                    {s.title} —{' '}
                    {
                      {
                        excluded: 'исключено словом',
                        all_day: 'на весь день',
                        duplicate: 'дубль',
                        calendar: 'календарь',
                      }[s.reason]
                    }
                  </li>
                ))}
            </ul>
          </details>
        )}
        {send.isError && <p className="text-error small">{send.error.message}</p>}
        {send.isSuccess && (
          <p className="small">
            {send.data.sent > 0
              ? `Отправлено: ${send.data.sent}.`
              : 'Сообщение пустое, не отправлено.'}
          </p>
        )}
        <div className="actions-row">
          <button
            type="button"
            className="btn"
            disabled={send.isPending || !preview.data?.text || changed}
            onClick={() => {
              if (window.confirm('Отправить сообщение на завтра получателям сейчас?'))
                send.mutate();
            }}
          >
            {send.isPending ? 'Отправляю…' : 'Отправить сейчас'}
          </button>
        </div>
        {data.lastSentFor && (
          <p className="muted small">Последняя вечерняя отправка — на {data.lastSentFor}.</p>
        )}
      </div>
    </section>
  );
}
